import {
  getRoutePath,
  getShopifyAppProxyRoutePath,
  isShopifyAppProxySurface,
  type HostingServingSurface,
} from '../utils/domain';
import { getAuthorizationHeader, redeemAndApplyLoginCode, signOut } from './index';

declare global {
  interface Window {
    /**
     * The App-Proxy skeleton's injected blob. Declaration-merged with the copy in
     * landing-page's `routePath.ts`; TypeScript requires the two to be
     * IDENTICAL, so the D-42 fields below must be kept in sync with it. (They
     * drifted once — `vite build` does not typecheck, so the mismatch only
     * showed up under an explicit `tsc --noEmit`.)
     */
    __BC_BOOTSTRAP__?: {
      shop?: string | null;
      loggedInCustomerId?: string | null;
      customerAccountsVersion?: 'new' | 'classic' | null;
      authType?: 'stackline' | 'shopify' | 'no_auth' | null;
      customerFirstName?: string | null;
      [key: string]: unknown;
    };
  }
}

/**
 * Per-tab debounce key for the client-initiated Shopify-login trigger.
 * Set only on non-success SSO outcomes. Cleared on explicit sign-in and BC sign-out.
 */
export const SHOPIFY_LOGIN_ATTEMPT_KEY = 'bc-shopify-login-attempted';

/** Clear the per-tab auto-SSO debounce (safe when sessionStorage is unavailable). */
export function clearShopifyLoginAttempt(): void {
  try {
    window.sessionStorage.removeItem(SHOPIFY_LOGIN_ATTEMPT_KEY);
  } catch {
    // sessionStorage unavailable — ignore.
  }
}

type ShopifyLoginResponse = { status?: string; bcLoginCode?: string; reason?: string };

async function fetchShopifyLogin(servingSurface?: HostingServingSurface | null): Promise<ShopifyLoginResponse | null> {
  const routePath = getShopifyAppProxyRoutePath(servingSurface);
  if (!routePath) return null;
  try {
    const res = await fetch(`${window.location.origin}${routePath}/shopify-login`, {
      method: 'GET',
      headers: { accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!res.ok) {
      console.warn('[shopifyLogin] shopify-login HTTP', res.status);
      return null;
    }
    return (await res.json()) as ShopifyLoginResponse;
  } catch (err) {
    console.error('[shopifyLogin] shopify-login fetch failed:', err);
    return null;
  }
}

async function reverseSyncSignOut(reason: string): Promise<boolean> {
  clearShopifyLoginAttempt();
  console.info('[shopifyLogin] reverse sync sign-out', { reason });
  try {
    await signOut();
  } catch (err) {
    console.error('[shopifyLogin] sync sign-out failed:', err);
    return false;
  }
  window.location.reload();
  return true;
}

export type MaybeStartShopifyLoginArgs = {
  signedIn: boolean;
  /**
   * From brand app config / bootstrap. Required for theme-embed SSO when
   * `__BC_ROUTE_PATH__` is not injected — DOM shape alone must not enable
   * App Proxy traffic (PetArmor / brandclub_domain embeds).
   */
  servingSurface?: HostingServingSurface | null;
};

/**
 * Client-initiated Shopify login trigger (Plan 4 / D-17, D-18).
 * Shared by landing-page and bc-checkout-shop-app (embed bubble).
 *
 * @returns true if a navigation/reload was started (caller should stop boot work).
 */
export async function maybeStartShopifyLogin({
  signedIn,
  servingSurface,
}: MaybeStartShopifyLoginArgs): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  if (!isShopifyAppProxySurface(servingSurface)) return false;

  const bootstrap = window.__BC_BOOTSTRAP__;
  const customerId = bootstrap?.loggedInCustomerId;
  const hasBootstrap = Boolean(bootstrap);

  if (signedIn) {
    // Liquid/bootstrap explicitly reports guest → drop Cognito session.
    if (hasBootstrap && !customerId) {
      return reverseSyncSignOut('bootstrap_guest');
    }
    // Theme embed often has no bootstrap (old checkout-bar inject). Probe App Proxy:
    // only reverse-sync on the definitive "not logged into Shopify" reason — never on
    // link/mint failures, which also return status no_session.
    if (!hasBootstrap) {
      const data = await fetchShopifyLogin(servingSurface);
      if (data?.reason === 'not_logged_in_to_shopify') {
        return reverseSyncSignOut('proxy_no_shopify_session');
      }
    }
    return false;
  }

  // Bootstrap explicitly says guest — skip. Otherwise try shopify-login; App Proxy
  // forwards `logged_in_customer_id` when the shopper is Shopify-signed-in.
  if (hasBootstrap && customerId == null) return false;

  const url = new URL(window.location.href);
  if (url.searchParams.has('bcLoginCode')) return false;

  try {
    if (customerId && window.sessionStorage.getItem(SHOPIFY_LOGIN_ATTEMPT_KEY) === String(customerId)) {
      console.info('[shopifyLogin] auto-SSO suppressed (debounce)', { customerId });
      return false;
    }
  } catch {
    // sessionStorage unavailable — proceed without the debounce.
  }

  try {
    console.info('[shopifyLogin] auto-SSO starting', { customerId: customerId ?? 'proxy' });
    const data = await fetchShopifyLogin(servingSurface);
    if (!data) return false;
    if (data.status === 'success' && data.bcLoginCode) {
      clearShopifyLoginAttempt();
      const applied = await redeemAndApplyLoginCode({ loginCode: data.bcLoginCode });
      if (applied) {
        window.location.reload();
        return true;
      }
      url.searchParams.set('bcLoginCode', data.bcLoginCode);
      window.location.assign(url.toString());
      return true;
    }
    console.info('[shopifyLogin] auto-SSO non-success', {
      customerId: customerId ?? 'unknown',
      status: data.status,
      reason: data.reason,
    });
    if (customerId) {
      try {
        window.sessionStorage.setItem(SHOPIFY_LOGIN_ATTEMPT_KEY, String(customerId));
      } catch {
        // sessionStorage unavailable — ignore.
      }
    }
  } catch (err) {
    console.error('[shopifyLogin] trigger failed:', err);
  }
  return false;
}

/**
 * Where to send a signed-out shopper who asks the concierge to sign them in,
 * for hosts that cannot run the inline phone/OTP form themselves.
 *
 * The inline form lives in the landing-page bundle (it needs Amplify + the
 * Redux profile reload), so the EMBED bubble — which mounts the same concierge
 * from `bc-checkout-shop-app` — has no `inlineSignIn` to call. Without a
 * destination, `PromptLoginPart` rendered nothing at all and a shopper who
 * asked the bubble about an order was told to "sign in here" with nothing to
 * click. This is that "here".
 *
 * `shopify` → the store's own customer login, returning to the page the
 * shopper is on (same classic/new split as landing-page `getShopifyLoginUrl`;
 * new customer accounts ignore classic's `return_url`).
 * `stackline` → the Brandclub phone-OTP page, under the App-Proxy sub-path
 * when there is one (D-44 serves the whole login state machine there).
 *
 * Returns null when there is nowhere to go — `no_auth` stores have no sign-in.
 */
export function getConciergeSignInUrl(authType?: 'stackline' | 'shopify' | 'no_auth' | null): string | null {
  if (typeof window === 'undefined') return null;
  const resolved = authType ?? window.__BC_BOOTSTRAP__?.authType ?? null;
  if (resolved === 'no_auth') return null;
  if (resolved === 'shopify') {
    const version = window.__BC_BOOTSTRAP__?.customerAccountsVersion;
    const entry = version === 'classic' ? '/account/login?return_url=' : '/customer_authentication/login?return_to=';
    return `${entry}${encodeURIComponent(`${window.location.pathname}${window.location.search}`)}`;
  }
  return `${getRoutePath()}/signin`;
}
